import { EditableProps } from "@/lib/fields"
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone"
import { ActionIcon } from "@mantine/core"
import { useEffect, useRef, useState } from "react"
import AvatarEditor from "react-avatar-editor"
import { store } from "@/stores/stores"
import { useUncontrolled } from "@mantine/hooks"
import { cn } from "@/lib/utils"
import AvatarUser from "@/components/base/AvatarUser"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import FormViewActions from "../FormView/FormViewActions"

const dropzoneContainerCN = `
          fc group cursor-pointer rounded-xl bordered p-8 h-67

          hover:border-secondary
        `

export function FieldEditAvatar(props: EditableProps) {
  const openRef = useRef<() => void>(null)
  const dummyRef = useRef<any>(null)
  const editorRef = useRef<any>(null)
  const [removing, setRemoving] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    return () => {
      store.app.editingAvatar = false
    }
  }, [])

  const [scale, setScale] = useState(0)
  const [avatarFile, setAvatarFile] = useState<(File & { preview: string }) | null>(null)
  const [_value, handleChange] = useUncontrolled({
    ...props.inputProps,
  })

  // Editing avatar
  if (avatarFile) {
    return (
      <div className="mb-8 fc size-full flex-col gap-2">
        <div
          onWheel={(e) => {
            setScale((prevScale) => {
              const newScale = prevScale + (e.deltaY > 0 ? -2 : 2)
              return Math.min(30, Math.max(0, newScale))
            })
          }}
        >
          <AvatarEditor
            ref={editorRef}
            image={avatarFile.preview}
            width={256}
            height={256}
            border={50}
            // color={theme === "dark" ? [27, 28, 29, 0.6] : [255, 255, 255, 0.6]} // RGBA
            color={[255, 255, 255, 0.6]} // RGBA
            scale={1 + scale / 10}
            rotate={0}
            borderRadius={999}
          />
        </div>
        <div className="mb-4 flex w-full items-center gap-3 px-10">
          <ActionIcon color="dark" variant={"subtle"} size={"lg"} type="button" onClick={() => setScale((s) => s - 1)}>
            <IconIcRoundMinus className="size-6" />
          </ActionIcon>
          <Slider
            value={[scale]}
            max={30}
            min={0}
            step={1}
            className="flex-1"
            onValueChange={(value) => {
              setScale(value[0])
            }}
          />
          {/* <Slider
            label={null}
            size={"lg"}
            min={0}
            max={30}
            step={1}
            className="flex-1"
            value={scale}
            onChange={(value) => setScale(value)}
          /> */}
          <ActionIcon color="dark" variant={"subtle"} size={"lg"} type="button" onClick={(e) => setScale((s) => s + 1)}>
            <IconMaterialSymbolsAdd className="size-6" />
          </ActionIcon>
        </div>
        <FormViewActions
          submitting={submitting}
          onCancel={() => {
            store.app.editingAvatar = false
            setAvatarFile(null)
          }}
          confirmButtonProps={{
            type: "button",
            onClick: async () => {
              try {
                setSubmitting(true)
                const canvasScaled = editorRef?.current?.getImageScaledToCanvas().toDataURL()
                const blob = await fetch(canvasScaled).then((res) => res.blob())
                const file = new File([blob], avatarFile.name, { type: blob.type })
                props.onSubmit?.({ value: file })
              } catch (error) {
                setSubmitting(false)
              }
            },
          }}
          className="px-6"
        />
      </div>
    )
  }

  return (
    <>
      <Dropzone
        onDrop={(files) => {
          store.app.editingAvatar = true
          const fileWithPreview = Object.assign(files[0], {
            preview: URL.createObjectURL(files[0]),
          })

          setAvatarFile(fileWithPreview)
          handleChange(fileWithPreview)
        }}
        onReject={(files) => {
          // TODO: Chamar toast de error
          console.log("rejected files", files)
        }}
        className="mt-1"
        maxSize={10 * 1024 ** 2}
        accept={IMAGE_MIME_TYPE}
        maxFiles={1}
        openRef={openRef}
      >
        <Dropzone.Accept>
          <div className={cn(dropzoneContainerCN, "border-secondary")}>
            <div className="fc size-38 items-center justify-center rounded-full bg-ground-hover">
              <IconLineMdUploadingLoop className="size-16 text-secondary" />
            </div>
          </div>
        </Dropzone.Accept>
        <Dropzone.Reject>
          <div className={cn(dropzoneContainerCN, "!border-red-400 bg-red-50")}>
            <div className="fc size-38 items-center justify-center rounded-full bg-red-100">
              <IconHugeiconsCancelCircle className="size-16 text-red-500" />
            </div>
          </div>
        </Dropzone.Reject>
        <Dropzone.Idle>
          <div className={cn(dropzoneContainerCN)}>
            <div className={`relative fc flex-col transition-all duration-125 ease-in-out hover:scale-105`}>
              <AvatarUser src={props.inputProps.defaultValue} className="size-38 text-3xl" name={props.extra?.name} />
            </div>
          </div>
        </Dropzone.Idle>
      </Dropzone>
      <div className="mt-4 fc gap-4">
        <Button onClick={() => openRef.current?.()} size="lg">
          <IconHugeiconsEdit02 />
          Change
        </Button>
        <Button
          variant="ghost"
          loading={removing}
          onClick={() => {
            setRemoving(true)
            handleChange(null)
            dummyRef.current?.click()
          }}
          size="lg"
        >
          <IconHugeiconsDelete03 />
          Remove
        </Button>
        <button type="submit" className="hidden" ref={dummyRef} />
      </div>
    </>
  )
}
